import { catchError, EMPTY, Observable, throwError, finalize } from 'rxjs';

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ErrorDetails } from '../models/error-details.model';

import { SnackbarService } from '../../services/snackbar.service';
import {
  PASS_ERROR_HEADER,
  TOKEN_EXPIRED_HEADER_NAME,
  API_AUTHENTICATION_ERROR_MESSAGE,
  USER_NOT_CREATED_ERROR_MESSAGE,
  API_SERVER_ERROR_MESSAGE,
  API_UNKNOWN_ERROR_MESSAGE,
  X_USER_NOT_CREATED_HEADER_NAME,
  COMPONENT_ID_HEADER,
} from '../consts/api.const';
import { LoadingService } from '../../services/loading.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private snackbarService: SnackbarService,
    private loadingService: LoadingService,
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let shouldThrowOutside = false;
    let processedRequest = request.clone();
    let componentId: string | null = null;

    if (processedRequest.headers.has(COMPONENT_ID_HEADER)) {
      componentId = processedRequest.headers.get(COMPONENT_ID_HEADER);
      processedRequest = request.clone({
        headers: processedRequest.headers.delete(COMPONENT_ID_HEADER),
      });
    }

    if (processedRequest.headers.has(PASS_ERROR_HEADER)) {
      processedRequest = request.clone({
        headers: processedRequest.headers.delete(PASS_ERROR_HEADER),
      });

      shouldThrowOutside = true;
    }

    return next.handle(processedRequest).pipe(
      finalize(() => {
        if (componentId) {
          this.loadingService.stopLoading(componentId);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const isTokenExpired = error.headers.has(TOKEN_EXPIRED_HEADER_NAME);

        if (!isTokenExpired) {
          this.handleError(error);

          return shouldThrowOutside ? throwError(() => error) : EMPTY;
        }

        return throwError(() => error);
      }),
    );
  }

  private handleError(error: HttpErrorResponse): void {
    let errorsDetails: ErrorDetails;

    if (typeof error.error === 'string') {
      try {
        errorsDetails = JSON.parse(error.error);
      } catch {
        errorsDetails = {
          statusCode: error.status,
          message: error.error,
        };
      }
    } else if (typeof error.error === 'object') {
      errorsDetails = error.error ?? {};
    } else {
      errorsDetails = {
        statusCode: error.status,
        message: error.error,
      };
    }

    const isErrorDetails = 'statusCode' in errorsDetails && 'message' in errorsDetails;

    const errorResponseCode = isErrorDetails ? errorsDetails.statusCode : error.status;
    const errorResponseMessage = isErrorDetails ? errorsDetails.message : '';

    const isClientError = errorResponseCode >= 400 && errorResponseCode <= 499;
    const isServerError = errorResponseCode >= 500;

    const isRegistrationPage = this.router.url.includes('signup');
    const isLoginPage = this.router.url.includes('login');

    const isAuthError = errorResponseCode === 401 && (isRegistrationPage || isLoginPage);
    const isForbiddenError = errorResponseCode === 403;
    const isUserNotCreated = error.headers.has(X_USER_NOT_CREATED_HEADER_NAME);

    let customErrorMessage: string = '';

    if (isClientError) {
      if (isAuthError) {
        customErrorMessage = API_AUTHENTICATION_ERROR_MESSAGE;

        if (!isRegistrationPage && !isLoginPage) {
          this.router.navigate(['login']);
        }
      } else if (isForbiddenError && isUserNotCreated) {
        customErrorMessage = USER_NOT_CREATED_ERROR_MESSAGE;
        setTimeout(() => {
          this.oktaAuth.signOut();
        }, 2000);
      } else {
        customErrorMessage = API_UNKNOWN_ERROR_MESSAGE;
      }
    } else if (isServerError) {
      customErrorMessage = API_SERVER_ERROR_MESSAGE;
    } else {
      customErrorMessage = API_UNKNOWN_ERROR_MESSAGE;
    }

    const notificationType = isClientError ? 'warning' : 'error';
    const notificationTitle = isClientError ? 'Warning' : 'Error';

    this.snackbarService.openCustom({
      message: errorResponseMessage || customErrorMessage || API_UNKNOWN_ERROR_MESSAGE,
      title: notificationTitle,
      type: notificationType,
      action: 'Close',
    });
  }
}
